import { useMemo } from "react"

import { Button, spacing } from "@minuthq/meatball-ui-react"
import { Badge } from "@minuthq/meatball-ui-react"

import { ParadiseCreateQuoteDialog } from "src/components/Paradise/ParadiseQuotes/ParadiseCreateQuoteDialog/ParadiseCreateQuoteDialog"
import { ParadiseQuotesTable } from "src/components/Paradise/ParadiseQuotes/ParadiseQuotesTable"
import { useParadiseQuotesFilter } from "src/components/Paradise/ParadiseQuotes/useParadiseQuotesFilter"
import {
  ParadiseLayout,
  ParadiseTitle,
} from "src/components/Paradise/sharedStyles"
import { parseParadiseOrdersSortField } from "src/data/paradise/paradiseOrders/logic/paradiseOrderLogic"
import { useFetchParadiseQuotes } from "src/data/paradise/paradiseQuotes/paradiseQuoteQueries"
import { useFetchParadiseUsers } from "src/data/paradise/paradiseUsers/queries/paradiseUserQueries"
import { useSearchParams } from "src/router/useSearchParams"
import { useSortParam } from "src/ui/GridTable/useTableColumns/useSortParam"
import { Titlebar } from "src/ui/Layout/Titlebar"
import { unique } from "src/utils/genericUtil"

export function ParadiseQuotes() {
  const { searchParams, setSearchParams } = useSearchParams({
    keys: [
      {
        key: "show_create_quote_dialog",
        type: "boolean",
      },
    ],
  })

  const { sortValue, setSortValue } = useSortParam()
  const { filter, sortedFilters, setFilter, setOffset, offset } =
    useParadiseQuotesFilter()

  const limit = 50
  const fetchParadiseQuotes = useFetchParadiseQuotes({
    filter: {
      limit: limit,
      offset,
      sales_rep_user_ids: filter.quotes_sales_rep_user_ids || undefined,
      expires_after: filter.quotes_expires_after || undefined,
      expires_before: filter.quotes_expires_before || undefined,
      status: filter.quotes_status || undefined,
      sort_by: parseParadiseOrdersSortField(sortValue?.id)
        ? sortValue?.id
        : undefined,
      sort_order: sortValue?.order || undefined,
    },
    options: {
      keepPreviousData: true,
    },
  })

  const userIds = [
    fetchParadiseQuotes.data?.quotes.map((quote) => quote.sales_rep_user_id),
    fetchParadiseQuotes.data?.quotes.map((quote) => quote.customer_id),
  ]
    .flat()
    .filter((id) => id !== undefined)
  const fetchParadiseUsers = useFetchParadiseUsers({
    filter: {
      ids: unique(userIds).join(",") || undefined,
    },
    options: {
      enabled: !!fetchParadiseQuotes.data?.paging.total_count,
      keepPreviousData: true,
    },
  })

  const combinedQuotesWithUsers = useMemo(() => {
    if (!fetchParadiseQuotes.data?.quotes) {
      return []
    }

    return fetchParadiseQuotes.data?.quotes.map((quote) => ({
      ...quote,
      salesRep: fetchParadiseUsers.data?.users.find(
        (user) => user.id === quote.sales_rep_user_id
      ),
      customer: fetchParadiseUsers.data?.users.find(
        (user) => user.id === quote.customer_id
      ),
    }))
  }, [fetchParadiseQuotes.data?.quotes, fetchParadiseUsers.data?.users])

  return (
    <ParadiseLayout>
      <Titlebar
        bottomMargin={spacing.S}
        title={
          <ParadiseTitle>
            Quotes
            <Badge color="info">
              Total:{" "}
              {fetchParadiseQuotes.isSuccess &&
                fetchParadiseQuotes.data?.paging.total_count}
            </Badge>
          </ParadiseTitle>
        }
        actionBar={
          <div>
            <Button
              onClick={() => setSearchParams("show_create_quote_dialog", true)}
              size="small"
            >
              Create quote
            </Button>
          </div>
        }
        size="unset"
      />

      <ParadiseQuotesTable
        quotesWithUsers={combinedQuotesWithUsers}
        filter={filter}
        setFilter={setFilter}
        sortedFilters={sortedFilters}
        sort={sortValue}
        setSort={setSortValue}
        limit={limit}
        offset={offset ?? 0}
        setOffset={setOffset}
        totalCount={fetchParadiseQuotes.data?.paging.total_count ?? 0}
        isLoading={fetchParadiseQuotes.isLoading}
        isLoadingMore={fetchParadiseQuotes.isPreviousData}
      />

      <ParadiseCreateQuoteDialog
        open={!!searchParams.show_create_quote_dialog}
        onClose={() => setSearchParams("show_create_quote_dialog", false)}
      />
    </ParadiseLayout>
  )
}
